import { useEffect, useRef, useState, type ReactNode } from 'react'
import { api, ErroreApi } from '../servizi/api.ts'
import { useSessione } from '../servizi/sessione.tsx'
import { Attesa, Avviso, Pie } from '../componenti/Pezzi.tsx'
import { Bottone, Campo } from '../componenti/base.tsx'

/**
 * Le pagine che si aprono dai collegamenti delle email, senza sessione: accettare un invito,
 * scegliere una password nuova, entrare con il collegamento. Fino al 17/10/2026 le email
 * portavano a indirizzi che l'applicazione non conosceva, e chi era stato invitato non aveva
 * modo di entrare.
 */

/** Percorsi che si aprono senza sessione. Gli altri portano alla pagina di accesso. */
export function ePercorsoDiIngresso(percorso: string): boolean {
  return ['/accesso/invito', '/accesso/nuova-password', '/accesso/collegamento'].includes(percorso)
}

export function Ingresso() {
  const percorso = window.location.pathname
  const token = new URLSearchParams(window.location.search).get('t') ?? ''
  if (percorso === '/accesso/invito') return <AccettaInvito token={token} />
  if (percorso === '/accesso/nuova-password') return <NuovaPassword token={token} />
  return <Collegamento token={token} />
}

/** Torna alla radice senza ricaricare: dopo l'ingresso si apre il cruscotto, non questo indirizzo. */
function vaiAllInizio() {
  window.history.replaceState(null, '', '/')
}

function Cornice({ titolo, occhiello, children }: { titolo: string; occhiello?: string; children: ReactNode }) {
  return (
    <div className="accesso">
      <div className="riquadro">
        <div className="scheda">
          <div className="stemma">
            <img src="/immagini/cidis-logo.png" alt="C.I.D.I.S. Impresa Sociale ETS" />
          </div>
          <h1>{titolo}</h1>
          {occhiello && <p className="occhiello">{occhiello}</p>}
          {children}
        </div>
        <Pie />
      </div>
    </div>
  )
}

function TornaAllAccesso() {
  return (
    <div className="accesso-uscita">
      <Bottone variante="discreto" larghezzaPiena onClick={() => { window.location.assign('/') }}>
        Vai alla pagina di accesso
      </Bottone>
    </div>
  )
}

const REGOLE_PASSWORD = 'Almeno 12 caratteri. Una frase che ricordi funziona meglio di una parola complicata.'

/** Password scelta due volte: la seconda serve solo a evitare un errore di battitura. */
function SceltaPassword({ password, ripetuta, cambiaPassword, cambiaRipetuta }: {
  password: string; ripetuta: string; cambiaPassword: (v: string) => void; cambiaRipetuta: (v: string) => void
}) {
  const diverse = ripetuta.length > 0 && ripetuta !== password
  return (
    <>
      <Campo etichetta="Scegli una password" tipo="password" valore={password} cambia={cambiaPassword}
             autoComplete="new-password" obbligatorio aiuto={REGOLE_PASSWORD} />
      <Campo etichetta="Scrivila di nuovo" tipo="password" valore={ripetuta} cambia={cambiaRipetuta}
             autoComplete="new-password" obbligatorio
             errore={diverse ? 'Le due password non coincidono.' : null} />
    </>
  )
}

/**
 * Perche' l'invito non si apre, detto per l'invito. Il 404 dell'API vale per qualunque cosa che non
 * c'e' (servizi/api.ts ne fa una frase sugli incarichi), ma qui chi legge non e' ancora entrato: un
 * collegamento che non corrisponde a nessun invito e' quasi sempre copiato male (19/10/2026).
 */
function messaggioInvito(e: unknown): string {
  if (!(e instanceof ErroreApi)) return 'Non riesco a leggere l’invito. Riprova fra poco.'
  if (e.codice === 'INVITO_INCOMPLETO') return e.message
  if (e.stato === 404) {
    return 'Questo collegamento non corrisponde a nessun invito. Aprilo di nuovo dall’email e controlla di averlo '
      + 'copiato per intero; se non funziona ancora, chiedi a chi ti ha invitato di mandartene uno nuovo.'
  }
  return e.message
}

type Invito = { nome: string; email: string; chiuso: string | null; nome_utente?: string | null; per_recupero?: boolean }

function AccettaInvito({ token }: { token: string }) {
  const { aggiorna } = useSessione()
  /** `nome_utente` (520): chi entra con la casella CIDIS, la parte prima della chiocciola. */
  const [invito, setInvito] = useState<Invito | null>(null)
  const [erroreCaricamento, setErroreCaricamento] = useState<string | null>(null)
  const [codice, setCodice] = useState('')
  const [password, setPassword] = useState('')
  const [ripetuta, setRipetuta] = useState('')
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  useEffect(() => {
    if (!token) { setErroreCaricamento('Nel collegamento manca una parte: aprilo di nuovo dall’email, per intero.'); return }
    api.get<Invito>(`/auth/invito?t=${encodeURIComponent(token)}`)
      .then(setInvito)
      .catch((e) => setErroreCaricamento(messaggioInvito(e)))
  }, [token])

  async function entra() {
    setErrore(null)
    if (password !== ripetuta) { setErrore('Le due password non coincidono.'); return }
    setInCorso(true)
    try {
      await api.post('/auth/invito/accetta', { token, codice, password })
      vaiAllInizio()
      await aggiorna()
    } catch (e) {
      setErrore(e instanceof ErroreApi && (e.stato === 404 || e.codice === 'INVITO_INCOMPLETO')
        ? messaggioInvito(e) : e instanceof ErroreApi ? e.message : 'Non è andata a buon fine. Riprova fra poco.')
    } finally {
      setInCorso(false)
    }
  }

  if (erroreCaricamento) {
    return (
      <Cornice titolo="Invito">
        <Avviso tipo="errore">{erroreCaricamento}</Avviso>
        <TornaAllAccesso />
      </Cornice>
    )
  }
  if (!invito) return <div className="accesso"><Attesa /></div>
  if (invito.chiuso) {
    return (
      <Cornice titolo={`Ciao ${invito.nome}`}>
        <Avviso tipo="attenzione">{invito.chiuso}</Avviso>
        <TornaAllAccesso />
      </Cornice>
    )
  }

  return (
    <Cornice titolo={`Ciao ${invito.nome}`}
             occhiello={invito.per_recupero ? 'Scegli la password nuova' : 'Ti hanno invitato nel gestionale di CIDIS'}>
      <p className="accesso-nota">
        {invito.nome_utente
          ? <>Il tuo nome utente è <strong>{invito.nome_utente}</strong>: nella pagina di accesso basta scrivere quello.</>
          : <>Entrerai con l’indirizzo <strong>{invito.email}</strong>.</>}
        {' '}Mancano due cose: il codice di sei cifre che ti ha dato a voce chi ti ha invitato, e una password che
        sceglierai adesso.
      </p>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <form onSubmit={(e) => { e.preventDefault(); void entra() }}>
        <div className="campo-codice">
          <Campo etichetta="Codice ricevuto a voce" valore={codice} obbligatorio maxLength={6}
                 cambia={(v) => setCodice(v.replace(/\D/g, ''))} autoComplete="one-time-code" />
        </div>
        <SceltaPassword password={password} ripetuta={ripetuta} cambiaPassword={setPassword} cambiaRipetuta={setRipetuta} />
        <Bottone tipo="submit" variante="primario" larghezzaPiena inCorso={inCorso}
                 disabled={codice.length !== 6 || password.length < 12 || password !== ripetuta}>
          Entra
        </Bottone>
      </form>
      <p className="accesso-nota">Se non hai il codice, chiedilo a chi ti ha invitato: non arriva per email apposta.</p>
    </Cornice>
  )
}

function NuovaPassword({ token }: { token: string }) {
  const [password, setPassword] = useState('')
  const [ripetuta, setRipetuta] = useState('')
  const [fatto, setFatto] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  async function salva() {
    setErrore(null)
    if (password !== ripetuta) { setErrore('Le due password non coincidono.'); return }
    setInCorso(true)
    try {
      await api.post('/auth/password/reimposta', { token, password })
      setFatto(true)
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine. Riprova fra poco.')
    } finally {
      setInCorso(false)
    }
  }

  if (fatto) {
    return (
      <Cornice titolo="Password cambiata">
        <Avviso tipo="successo">Fatto: da adesso entri con la password nuova.</Avviso>
        <TornaAllAccesso />
      </Cornice>
    )
  }
  return (
    <Cornice titolo="Nuova password" occhiello="Il collegamento vale poco tempo e una volta sola">
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <form onSubmit={(e) => { e.preventDefault(); void salva() }}>
        <SceltaPassword password={password} ripetuta={ripetuta} cambiaPassword={setPassword} cambiaRipetuta={setRipetuta} />
        <Bottone tipo="submit" variante="primario" larghezzaPiena inCorso={inCorso}
                 disabled={!token || password.length < 12 || password !== ripetuta}>
          Salva la password
        </Bottone>
      </form>
      <TornaAllAccesso />
    </Cornice>
  )
}

function Collegamento({ token }: { token: string }) {
  const { aggiorna } = useSessione()
  const [errore, setErrore] = useState<string | null>(null)
  // Il collegamento vale una volta sola: in sviluppo React monta due volte, e la seconda
  // chiamata troverebbe il collegamento gia' usato.
  const avviato = useRef(false)

  useEffect(() => {
    if (avviato.current) return
    avviato.current = true
    if (!token) { setErrore('Nel collegamento manca una parte: aprilo di nuovo dall’email, per intero.'); return }
    api.post('/auth/magic-link/consuma', { token })
      .then(async () => { vaiAllInizio(); await aggiorna() })
      .catch((e) => setErrore(e instanceof ErroreApi ? e.message : 'Non riesco a usare il collegamento. Riprova fra poco.'))
  }, [token, aggiorna])

  if (!errore) return <div className="accesso"><Attesa /></div>
  return (
    <Cornice titolo="Collegamento di accesso">
      <Avviso tipo="errore">{errore}</Avviso>
      <p className="accesso-nota">Il collegamento vale quindici minuti e si usa una volta sola. Puoi chiederne uno nuovo dalla pagina di accesso.</p>
      <TornaAllAccesso />
    </Cornice>
  )
}
